import {
  Combobox,
  Icon,
  IconButton,
  Link,
  Loader,
  Offscreen,
  Panel,
  PanelContent
} from '@deque/cauldron-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { type ComboboxValue } from '@deque/cauldron-react/lib/components/Combobox/ComboboxOption';
import ContentToast from '~/common/components/ContentToast';
import { useAuthContext } from '~/common/contexts/auth';
import { useEnterprises } from '~/common/contexts/enterprises';
import useV2EnterpriseMembers from '~/common/hooks/useV2EnterpriseMembers';
import { type EnterpriseMembers } from '~/common/utils/billing-client/client-v2';
import styles from './AddUsers.css';
import { TEAM_MEMBERS_PREVIOUS_ID } from './SettingSteps';

export interface AddUsersProps {
  userIds: string[];
  setUsersToAdd: (usersToAdd: string[]) => void;
  setUsersToRemove: (usersToAdd: string[]) => void;
}

type ComboboxOption = {
  label: string;
  value: string;
};

export function isAcceptedMember(
  enterpriseMember: EnterpriseMembers.PendingOrAcceptedMember
): enterpriseMember is EnterpriseMembers.AcceptedMember {
  return (
    (enterpriseMember as EnterpriseMembers.AcceptedMember).user_id !== undefined
  );
}

export const getComboboxLabel = (
  enterpriseMember: EnterpriseMembers.PendingOrAcceptedMember | null
): string => {
  if (!enterpriseMember) {
    return '';
  }

  return isAcceptedMember(enterpriseMember)
    ? `${enterpriseMember.first_name} ${enterpriseMember.last_name} (${enterpriseMember.email})`
    : enterpriseMember.email;
};

const getUserFromId = (
  enterpriseMembers: EnterpriseMembers.PendingOrAcceptedMember[] | null,
  userIdOrEmail: string
): EnterpriseMembers.PendingOrAcceptedMember | null => {
  const foundUser = enterpriseMembers?.find(enterpriseMember =>
    isAcceptedMember(enterpriseMember)
      ? enterpriseMember.user_id === userIdOrEmail
      : enterpriseMember.email === userIdOrEmail
  );

  return foundUser || null;
};

const convertEnterpriseMembersToComboboxOptions = (
  enterpriseMembers: EnterpriseMembers.PendingOrAcceptedMember[] | null,
  membersToAdd: string[]
): ComboboxOption[] => {
  const acceptedUsersAsComboboxOptions: ComboboxOption[] = [];
  if (enterpriseMembers) {
    for (const enterpriseMember of enterpriseMembers) {
      const isAccepted = isAcceptedMember(enterpriseMember);
      const optionValue = isAccepted
        ? enterpriseMember.user_id
        : enterpriseMember.email;

      if (membersToAdd.includes(optionValue)) {
        continue;
      }

      acceptedUsersAsComboboxOptions.push({
        label: getComboboxLabel(enterpriseMember),
        value: optionValue
      });
    }
  }

  return acceptedUsersAsComboboxOptions;
};

export const AddUsers: React.FC<AddUsersProps> = ({
  userIds: initiallySelectedUserIds,
  setUsersToAdd,
  setUsersToRemove
}) => {
  const { t } = useTranslation();

  const [membersToAdd, setMembersToAdd] = useState<string[]>(
    initiallySelectedUserIds
  );

  const { user } = useAuthContext();
  const {
    activeEnterprise,
    loading: loadingEnterprises,
    error: enterprisesError
  } = useEnterprises();

  const token = user?.token;
  const enterpriseId = activeEnterprise?.id;
  const {
    pendingOrAcceptedMembers,
    loading: loadingMembers,
    error: memberError
  } = useV2EnterpriseMembers({ token, enterpriseId });

  // Calculate users that have been added or removed
  useEffect(() => {
    const netAddedUsers: string[] = [];
    for (const memberToAdd of membersToAdd) {
      if (!initiallySelectedUserIds.includes(memberToAdd)) {
        netAddedUsers.push(memberToAdd);
      }
    }
    setUsersToAdd(netAddedUsers);

    const netRemovedUsers: string[] = [];
    for (const initiallySelectedUserId of initiallySelectedUserIds) {
      if (
        !membersToAdd.find(
          memberToFind => memberToFind === initiallySelectedUserId
        )
      ) {
        netRemovedUsers.push(initiallySelectedUserId);
      }
    }
    setUsersToRemove(netRemovedUsers);
  }, [membersToAdd]);

  // Precalculate combobox options
  const comboboxOptions = useMemo(
    () =>
      convertEnterpriseMembersToComboboxOptions(
        pendingOrAcceptedMembers,
        membersToAdd
      ),
    [pendingOrAcceptedMembers, membersToAdd]
  );

  // If there is an error, show Toast and exit
  if (enterprisesError || memberError) {
    return (
      <ContentToast show={true} type="caution">
        {t('Failed to load users.')}
      </ContentToast>
    );
  }

  const infoRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    infoRef.current?.focus();
  }, [infoRef.current]);

  // Show loader (with focus) while loading enterprise members
  const loaderRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    loaderRef.current?.focus();
  }, [loaderRef.current]);
  if (loadingEnterprises || loadingMembers) {
    return (
      <Loader label={t('Loading users...')} tabIndex={-1} ref={loaderRef} />
    );
  }

  const handleSelectionChange = ({
    value
  }: {
    target: HTMLElement;
    value: ComboboxValue;
    previousValue: ComboboxValue;
  }) => {
    if (value) {
      setMembersToAdd(currentMembersToAdd => [...currentMembersToAdd, value]);
    }
  };

  const removeMember = (userIdToRemove: string): void => {
    setMembersToAdd(currentMembersToAdd =>
      currentMembersToAdd.filter(
        memberToCheck => memberToCheck !== userIdToRemove
      )
    );
  };

  return (
    <div className={styles.addedUserWrapper}>
      {comboboxOptions.length === 0 && (
        <div className={styles.noUsersNotice} ref={infoRef} tabIndex={-1}>
          {t('All users have been added to the team')}
        </div>
      )}
      <Combobox
        label={t('Find and Add Members')}
        options={comboboxOptions}
        onSelectionChange={handleSelectionChange}
        key={new Date().getTime()} // random key to force regeneration of a combobox to avoid selection from "sticking" after re-render
      />
      <Link
        href={`#${TEAM_MEMBERS_PREVIOUS_ID}`}
        className={styles.skipLink}
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          document.getElementById(TEAM_MEMBERS_PREVIOUS_ID)?.focus();
        }}
      >
        <span className={styles.skipLinkLabel}>{t('Skip to')}</span>
        <span className={styles.skipLinkTarget}>
          <Icon type="triangle-right" />
          {t('Previous')}
        </span>
      </Link>

      {membersToAdd.length > 0 && (
        <Panel padding={false} className={styles.addedUserPanel}>
          {membersToAdd.map(userId => {
            const comboboxLabel = getComboboxLabel(
              getUserFromId(pendingOrAcceptedMembers, userId)
            );

            return (
              <PanelContent
                className={styles.addedUserPanelContent}
                key={userId}
                data-testid="selected-user"
              >
                <span>{comboboxLabel}</span>
                <span className={styles.spacer}></span>
                <IconButton
                  icon="trash"
                  label={
                    <>
                      {t('Delete')}
                      <Offscreen>{comboboxLabel}</Offscreen>
                    </>
                  }
                  tooltipProps={{ placement: 'bottom' }}
                  onClick={() => removeMember(userId)}
                />
              </PanelContent>
            );
          })}
        </Panel>
      )}
    </div>
  );
};

export default AddUsers;
